import React from 'react';
import { Box, Typography, Chip, alpha, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  RocketLaunch, TrendingUp, SwapHoriz, ShieldOutlined, Autorenew, Visibility,
  Block, AccountBalance, Speed, Straighten,
  ShowChart, BarChart, WaterDrop, Security,
  CheckCircleOutline, DoNotDisturb,
} from '@mui/icons-material';

const REGIME_CONFIG = {
  RISK_ON:       { color: '#10b981', icon: RocketLaunch, label: 'RISK ON',       viKey: 'regimeWatchlist.regime.riskOn' },
  RECOVERY:      { color: '#3b82f6', icon: TrendingUp,   label: 'RECOVERY',     viKey: 'regimeWatchlist.regime.recovery' },
  SIDEWAY:       { color: '#f59e0b', icon: SwapHoriz,    label: 'SIDEWAY',      viKey: 'regimeWatchlist.regime.sideway' },
  RISK_OFF:      { color: '#ef4444', icon: ShieldOutlined, label: 'RISK OFF',    viKey: 'regimeWatchlist.regime.riskOff' },
  ROTATION:      { color: '#8b5cf6', icon: Autorenew,    label: 'ROTATION',     viKey: 'regimeWatchlist.regime.rotation' },
  NARROW_RALLY:  { color: '#06b6d4', icon: Visibility,   label: 'NARROW RALLY', viKey: 'regimeWatchlist.regime.narrowRally' },
};

const SCORE_META = [
  { key: 'trend',     letter: 'T', labelKey: 'regimeWatchlist.score.trend',     Icon: ShowChart },
  { key: 'breadth',   letter: 'B', labelKey: 'regimeWatchlist.score.breadth',   Icon: BarChart },
  { key: 'liquidity', letter: 'L', labelKey: 'regimeWatchlist.score.liquidity', Icon: WaterDrop },
  { key: 'risk',      letter: 'R', labelKey: 'regimeWatchlist.score.risk',      Icon: Security },
];

const scoreColor = (v) => v >= 60 ? '#10b981' : v >= 40 ? '#f59e0b' : '#ef4444';
const scoreBg    = (v) => v >= 60 ? alpha('#10b981', 0.08) : v >= 40 ? alpha('#f59e0b', 0.08) : alpha('#ef4444', 0.08);

const RegimeBadge = ({ regime, strategies = [], totalCandidates = 0, interpretation = {}, regimeDetails = {}, notes, marketStateLabels, selloffOverride, selloffNote }) => {
  const { t } = useTranslation();
  const cfg = REGIME_CONFIG[regime] || REGIME_CONFIG['SIDEWAY'];
  const c = cfg.color;
  const { trade_bias, exposure, focus, avoid, risk_per_trade, sizing_mode, no_trade } = interpretation || {};

  const stateLabels = marketStateLabels || {};
  const hasScores = regimeDetails && (regimeDetails.trend_score != null || regimeDetails.breadth_score != null);

  const scoreItems = hasScores ? SCORE_META
    .map(meta => {
      const rawVal = regimeDetails[`${meta.key}_score`];
      if (rawVal == null) return null;
      const sl = stateLabels[meta.key];
      return { ...meta, val: rawVal, stateLabel: sl?.label || null };
    })
    .filter(Boolean) : [];

  return (
    <Box sx={{ bgcolor: 'white', border: `1px solid ${alpha(c, 0.18)}`, borderRadius: 1, mb: 2, overflow: 'hidden' }}>

      {/* ─── NO TRADE BANNER ─── */}
      {no_trade && (
        <Box sx={{
          px: 2.5, py: 1, bgcolor: '#fef2f2', borderBottom: '2px solid #ef4444',
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <Block sx={{ fontSize: 20, color: '#dc2626' }} />
          <Typography sx={{ fontSize: '0.88rem', fontWeight: 900, color: '#dc2626', letterSpacing: 0.3 }}>
            {t('regimeWatchlist.noTrade', 'NO TRADE')}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#991b1b' }}>
            — {t('regimeWatchlist.noTradeDesc', 'Market unfavorable, prioritize capital preservation')}
          </Typography>
        </Box>
      )}

      {/* ─── SELLOFF CAUTION BANNER ─── */}
      {selloffOverride === 'CAUTION' && selloffNote && (
        <Box sx={{
          px: 2.5, py: 1, bgcolor: '#fffbeb', borderBottom: '2px solid #f59e0b',
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <ShieldOutlined sx={{ fontSize: 20, color: '#d97706' }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#92400e' }}>
            {selloffNote}
          </Typography>
        </Box>
      )}

      {/* ═══ ROW 1: Regime + Budget + Count ═══ */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, pt: 1.5, pb: 1, gap: 2, flexWrap: 'wrap' }}>

        {/* Regime identity */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: 1, bgcolor: alpha(c, 0.10),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {React.createElement(cfg.icon, { sx: { fontSize: 21, color: c } })}
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: c, letterSpacing: -0.3 }}>
                {cfg.label}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>
                {t(cfg.viKey, cfg.label)}
              </Typography>
            </Box>
            {trade_bias && (
              <Typography sx={{ fontSize: '0.68rem', color: '#475569', fontWeight: 600, mt: 0.1 }}>
                {trade_bias}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Budget chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
          {exposure && (
            <Chip icon={<AccountBalance sx={{ fontSize: 13 }} />} label={exposure} size="small" sx={{
              height: 26, fontSize: '0.7rem', fontWeight: 800,
              bgcolor: alpha(c, 0.10), color: c,
              '& .MuiChip-label': { px: 0.8 }, '& .MuiChip-icon': { ml: 0.4, color: c },
            }} />
          )}
          {risk_per_trade && (
            <Chip icon={<Speed sx={{ fontSize: 13 }} />} label={risk_per_trade} size="small" sx={{
              height: 26, fontSize: '0.68rem', fontWeight: 700,
              bgcolor: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0',
              '& .MuiChip-label': { px: 0.7 }, '& .MuiChip-icon': { ml: 0.4, color: '#64748b' },
            }} />
          )}
          {sizing_mode && (
            <Chip icon={<Straighten sx={{ fontSize: 13 }} />} label={sizing_mode} size="small" sx={{
              height: 26, fontSize: '0.68rem', fontWeight: 700,
              bgcolor: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0',
              '& .MuiChip-label': { px: 0.7 }, '& .MuiChip-icon': { ml: 0.4, color: '#64748b' },
            }} />
          )}
        </Box>

        {/* Count (right-aligned) */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'baseline', gap: 0.5, flexShrink: 0 }}>
          <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: '#1e293b', lineHeight: 1, letterSpacing: -1 }}>
            {totalCandidates}
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600 }}>
            {t('regimeWatchlist.stocks', 'stocks')}
          </Typography>
        </Box>
      </Box>

      {/* ═══ ROW 2: Score pills + Strategy tags ═══ */}
      {(scoreItems.length > 0 || strategies.length > 0) && (
        <Box sx={{ px: 2.5, pb: 1.2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>

          {/* Score pills */}
          {scoreItems.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.8 }}>
              {scoreItems.map(s => {
                const clr = scoreColor(s.val);
                const bg = scoreBg(s.val);
                const quality = s.val >= 60 ? t('regimeWatchlist.positive', 'Positive') : s.val >= 40 ? t('regimeWatchlist.neutral', 'Neutral') : t('regimeWatchlist.negative', 'Negative');
                return (
                  <Tooltip key={s.key} title={`${t(s.labelKey, s.key)}: ${s.val.toFixed(0)}/100 — ${quality}`} arrow>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    px: 1, py: 0.4, borderRadius: 0.8, bgcolor: bg,
                    cursor: 'default',
                  }}>
                    {React.createElement(s.Icon, { sx: { fontSize: 14, color: clr } })}
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#475569' }}>
                      {t(s.labelKey, s.key)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, color: clr, fontFamily: 'monospace' }}>
                      {s.val.toFixed(0)}
                    </Typography>
                    {s.stateLabel && (
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: clr }}>
                        {s.stateLabel}
                      </Typography>
                    )}
                  </Box>
                  </Tooltip>
                );
              })}
            </Box>
          )}

          {/* Strategy tags */}
          {strategies.length > 0 && (
            <>
              {scoreItems.length > 0 && (
                <Box sx={{ width: '1px', height: 20, bgcolor: '#e5e7eb', flexShrink: 0 }} />
              )}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {strategies.map((s, i) => (
                  <Chip key={i} label={s.replace(/_/g, ' ')} size="small" sx={{
                    height: 22, fontSize: '0.6rem', fontWeight: 700,
                    bgcolor: alpha(c, 0.06), color: alpha(c, 0.85),
                    textTransform: 'uppercase', letterSpacing: 0.3,
                    '& .MuiChip-label': { px: 0.7 },
                  }} />
                ))}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ═══ ROW 3: Guidance ═══ */}
      {(focus || avoid || notes) && (
        <Box sx={{
          px: 2.5, py: 0.8, borderTop: `1px solid ${alpha(c, 0.08)}`, bgcolor: '#fafbfc',
          display: 'flex', gap: 2.5, alignItems: 'center', flexWrap: 'wrap',
        }}>
          {focus && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <CheckCircleOutline sx={{ fontSize: 14, color: '#10b981' }} />
              <Typography sx={{ fontSize: '0.7rem', color: '#334155', fontWeight: 500 }}>{focus}</Typography>
            </Box>
          )}
          {avoid && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <DoNotDisturb sx={{ fontSize: 14, color: '#ef4444' }} />
              <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500 }}>{avoid}</Typography>
            </Box>
          )}
          {notes && <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', ml: 'auto', fontStyle: 'italic' }}>{notes}</Typography>}
        </Box>
      )}
    </Box>
  );
};

export default RegimeBadge;
